import r2wc from '@r2wc/react-to-web-component';
import { StatsigProvider } from '@statsig/react-bindings';
import { useEffect, useMemo, useState } from 'react';

import { useEssentialProps } from '../../../apps/explore/components/AppWrapper/_app.hooks';
import { Header } from '../../../apps/explore/components/Header/Header';
import { MarketProvider } from '../../../apps/explore/providers/MarketProvider';
import { SideMenuProvider } from '../../../apps/explore/providers/SideMenuProvider';
import { TranslationProvider } from '../../../apps/explore/providers/TranslationProvider';
import { UserProvider } from '../../../apps/explore/providers/UserProvider';
import { getMarket } from '../../../apps/explore/utils/markets';
import { DEFAULT_SEMBO_MARKET } from './constants';
import { useStatsigClient, useTags } from './hooks';
import { WidgetStyles } from './WidgetStyles';

interface Props {
  'enable-navigation'?: boolean;
  'enable-side-menu'?: boolean;
}

const SemboHeader = ({
  'enable-navigation': isNavigationEnabled = false,
  'enable-side-menu': isSideMenuEnabled = false,
}: Props) => {
  const market = getMarket(window.location.host) ?? DEFAULT_SEMBO_MARKET;

  const options = useMemo(
    () => ({
      isNavigationEnabled,
      isSideMenuEnabled,
    }),
    [isNavigationEnabled, isSideMenuEnabled]
  );

  const { microcopy } = useEssentialProps({ market });
  const { client } = useStatsigClient(market);
  const tags = useTags(market, options);
  const [key, setKey] = useState(0);

  const openAuthModal = (origin: string) => {
    window.dispatchEvent(
      new CustomEvent('semboShowAuthModal', {
        detail: { origin },
        bubbles: true,
      })
    );
  };

  // update key when user logs in, to trigger a re-render of the component
  useEffect(() => {
    const updateKey = () => {
      setKey((prev) => prev + 1);
    };

    window.addEventListener('semboUserLoggedIn', updateKey);

    return () => {
      window.removeEventListener('semboUserLoggedIn', updateKey);
    };
  }, []);

  if (!microcopy) {
    return null;
  }

  return (
    <div key={key}>
      <MarketProvider market={market}>
        <StatsigProvider client={client}>
          <UserProvider>
            <TranslationProvider microcopy={microcopy} lang={market.lang}>
              <SideMenuProvider
                tags={tags}
                onAuthClick={() => openAuthModal('side-menu')}
              >
                <WidgetStyles />

                <Header
                  isNavigationEnabled={isNavigationEnabled}
                  isSideMenuEnabled={isSideMenuEnabled}
                  tags={tags}
                  onAuthClick={() => openAuthModal('header')}
                />
              </SideMenuProvider>
            </TranslationProvider>
          </UserProvider>
        </StatsigProvider>
      </MarketProvider>
    </div>
  );
};

customElements.define(
  'sembo-header',
  r2wc(SemboHeader, {
    props: {
      'enable-navigation': 'boolean',
      'enable-side-menu': 'boolean',
    },
  })
);
